/** Tuned values of one version: field id to the text form the game parses. */
export type TunableValues = Record<string, string>;

/** What the editor is handed when it announces itself. */
export interface EditorSession {
  applied: TunableValues;
  pending: TunableValues;
  layout: unknown | null;
  isOwner: boolean;
  language: string;
  jobRunning: boolean;
  organize: boolean;
}

/** Why the chat did not take the creator's request: a request of the project is queued or running, or sending failed. */
export type RequestFailure = 'busy' | 'failed';

export interface EditorHandlers {
  /** The session to load the editor with, or null when it cannot be read; the editor then works on its own. */
  session: () => Promise<EditorSession | null>;
  /** Settles once the values are stored or their failure is reported; never rejects. */
  save: (pending: TunableValues) => Promise<void>;
  expose: (text: string) => void;
  organize: () => void;
  /** Sends the creator's words to the project's chat; resolves to null once sent, never rejects. */
  request: (text: string) => Promise<RequestFailure | null>;
}

interface EditorMessage {
  source?: string;
  type?: string;
  pending?: TunableValues;
  text?: string;
}

const Channel = 'entire-editor';

/**
 * The page's half of the conversation with the editor the game build carries, for a page that shows one editor
 * frame at a time. A message counts only from the build's origin, and every reply goes back to that origin.
 * `ready`, `expose`, `organize` and `request` count only from this host's frame. A `save` counts from any frame of
 * the origin and goes to the host whose frame announced itself last: a frame the page removes sends its last change
 * as it goes, when its source may no longer be told apart and its host is already stopped. Saves are stored one after
 * another, and a `ready` is answered, or a `request` sent to the chat, only once every save received before it is
 * stored, so the request's job bakes them. A `request` the chat does not take is answered with `requestFailed` and
 * its `reason`. Nothing is sent to a frame before it announces itself; the `load` that answers its `ready` carries the
 * job state and the layout it missed until then.
 */
export class EditorHost {
  private static announcedLast: EditorHost | null = null;
  private static readonly announcedFrames = new WeakSet<Window>();
  private static saves: Promise<void> = Promise.resolve();

  private stopped = false;

  constructor(
    private readonly frame: () => Window | null,
    private readonly origin: string,
    private readonly handlers: EditorHandlers,
  ) {}

  /** The returned stop ends the conversation; the host still takes saves until another frame announces itself. */
  listen(): () => void {
    window.addEventListener('message', this.receive);
    return () => {
      this.stopped = true;
      if (EditorHost.announcedLast !== this) this.unlisten();
    };
  }

  sendJobState(running: boolean): void {
    this.send({ type: 'jobState', running });
  }

  sendLayout(layout: unknown): void {
    this.send({ type: 'layout', layout });
  }

  private receive = (event: MessageEvent): void => {
    if (event.origin !== this.origin) return;

    const message = event.data as EditorMessage | null;
    if (!message || message.source !== Channel) return;

    if (message.type === 'save') {
      if (EditorHost.announcedLast === this) this.store(message.pending ?? {});
      return;
    }

    const frame = this.frame();
    if (this.stopped || !frame || event.source !== frame) return;

    if (message.type === 'ready') {
      EditorHost.announcedFrames.add(frame);
      this.takeSaves();
      void this.answer();
    }
    if (message.type === 'expose' && message.text) this.handlers.expose(message.text);
    if (message.type === 'organize') this.handlers.organize();
    if (message.type === 'request' && message.text) void this.request(message.text);
  };

  private takeSaves(): void {
    const previous = EditorHost.announcedLast;
    EditorHost.announcedLast = this;
    if (previous?.stopped) previous.unlisten();
  }

  private store(pending: TunableValues): void {
    EditorHost.saves = EditorHost.saves.then(() => this.handlers.save(pending));
  }

  private async answer(): Promise<void> {
    await EditorHost.saves;
    const session = await this.handlers.session();
    if (session) this.send({ type: 'load', ...session });
  }

  private async request(text: string): Promise<void> {
    await EditorHost.saves;
    const failure = await this.handlers.request(text);
    if (failure) this.send({ type: 'requestFailed', reason: failure });
  }

  private unlisten(): void {
    window.removeEventListener('message', this.receive);
  }

  private send(payload: Record<string, unknown>): void {
    const frame = this.frame();
    if (frame && EditorHost.announcedFrames.has(frame)) frame.postMessage({ source: Channel, ...payload }, this.origin);
  }
}
